import {
  getCachedConfiguration,
  getCachedConfigurations,
  getConfiguration
} from "@nuskin/configuration-sdk";
import {
  events,
  StringService,
  util,
  SitesSessionStorageService,
  ShoppingContext,
  StoreFrontSponsorStorageService,
  PersonalOfferStorageService,
  dateFormatter,
  UrlService,
  RunConfigService,
  equinoxLocalStorage
} from "@nuskin/ns-util";
import {
  CartService,
  AdrService,
  FavoritesService,
  ProductStatus,
  Product as ShopProduct,
  EquinoxCartService
} from "@nuskin/ns-shop";
import QualificationService from "@nuskin/exclusive-offer-sdk";
import webLoyalty from "@nuskin/ns-loyalty-web";
import { getProp, getFullUrl } from "@nuskin/ns-common-lib";
import { PriceType } from "@nuskin/ns-product-lib";
import { ProductDataService } from "@nuskin/ns-product";
import { waitForConfig } from "../services/configHelper";
import { fetchPromotionalPrices } from "../services/EQPromotionService";

const PENDING_FAVORITE = "pendingFavorite";
const KEY_BREADCRUMB_BACK = "breadcrumbBack";
const BUNDLE = "BUNDLE";

/**
 * NsProductMixin
 *
 * How to use: This mixin provides all the logic to both NsProductDetail and
 * NsProductCard
 *
 * @event variant-select  When a variant selection occurs, this event emits a
 * function with params: (value, index)
 * @event quantity-select  When a quantity selection occurs, this event emits a
 * function with params: (value, index)
 * @event product-update  When a product updates, this event emits a function
 * with params: (product)
 * @event card-click  When clicking on the ProductCard, this event emits a
 * function with params: (product). If listener is NOT assigned, clicking the
 * card will go to the product detail page
 * @event added-to-cart After product is added to cart, this event emits a
 * function with params: ({sku, qty, product, referrer})
 * @event added-to-adr After product is added to cart, this event emits a
 * function with params: ({sku, qty, product, referrer})
 * @event favorite-click When clicking on Favorite Icon, this event emits a
 * function with params: ({sku, isFavorite})
 */
const NsProductMixin = {
  props: {
    /**
     * Hides ADR Price Always
     */
    hideAdrOverride: {
      type: Boolean,
      default: false
    },
    /**
     * Hides the Variant Selector always
     */
    hideVariantSelector: {
      type: Boolean,
      default: false
    },
    /**
     * Use if the parent needs to control the quantity
     */
    quantity: {
      type: Number,
      default: 1
    },
    /**
     * Product sku
     */
    sku: {
      type: String,
      default: ""
    },
    /**
     * Product-related translations
     */
    translations: {
      type: String,
      default: ""
    },
    /**
     * Loyalty Strings
     */
    loyaltyStrings: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Force product to be a ADR in cart
     */
    adrHasPriority: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /**
       * Determines the current sku being operated on (could be this.sku or a variant sku)
       */
      activeSku: this.sku,
      /**
       * The price the customer will pay when adding to subscription order
       */
      adrPrice: "",
      adrPriceType: PriceType.WADR,
      /**
       * Base product data
       */
      baseProduct: undefined,
      baseSku: "",
      checkedQualifications: false,
      description: "",
      /**
       * Determines if the "Add To Cart" button is disabled
       */
      disableAddToCart: true,
      /**
       * Determines if the "Add To ADR" button is disabled
       */
      disableAddToAdr: true,
      /**
       * Determines if the "Quantity" dropdown is disabled
       */
      disableQuantitySelector: true,
      eventName: "",
      /**
       * Exclusive offer text determined by user logged in and status.
       */
      exclusiveOfferMessage: "",
      hideAdr: false,
      hideLoyalty: false,
      image: {},
      imageSet: [],
      isBaseSku: false,
      isExclusive: false,
      isStorefront: false,
      isPersonalOffer: false,
      /**
       * Determines if the current product is a pending or current favorite
       */
      isFavorite: false,
      isSale: false,
      /**
       * Exclusive offer text determined by user logged in and status.
       */
      isVariantSku: false,
      loadFirstVariant: false,
      /**
       * Translations object
       */
      localTranslations: {},
      marketAttributes: {},
      maxPrice: "",
      minPrice: "",
      maxQuantity: 999,
      nettoWeight: "",
      /**
       * The price the customer will pay when adding to cart
       */
      originalPrice: "",
      originalPriceLabel: "",
      originalPriceType: PriceType.WRTL,
      points: "",
      price: "",
      priceType: PriceType.WRTL,
      /**
       * Price label for wholesale/retail when ADR is available
       */
      priceLabel: "",
      /**
       * Active poduct data
       */
      product: undefined,
      promotionPrice: null,
      promotionOriginalPrice: null,
      /**
       * Quantity of product selected
       */
      selectedQuantity: this.quantity,
      referrer: "productMixin",
      salesEventText: "",
      salesLabel: "",
      /**
       * Determines if retail or wholesale price will display as discounted (with strike-through) compared to retail price
       */
      // showOriginalCrossedOut: false,
      sizeWeight: "",
      statusMessage: "",
      statusMessageKey: "",
      title: "",
      userIsQualified: false,
      userLoyalty: undefined,
      variants: [],

      featureFlags: this.$NsProductFeatureFlagsService.featureFlags,

      activeSalesEvents: [],

      showVariantFromEq: false,
      listenersAdded: false
    };
  },
  computed: {
    runConfig() {
      return this.$NsProductAppService.runConfig;
    },
    locale() {
      return this.$NsProductAppService.locale;
    },
    marketConfig() {
      return this.$NsProductAppService.marketConfig;
    },
    isLoggedIn() {
      return this.$NsProductUserService.isLoggedIn;
    },
    user() {
      return this.$NsProductUserService.user;
    },
    userId() {
      return this.$NsProductUserService.userId;
    },
    isDistributor() {
      return this.$NsProductUserService.isDistributor;
    },
    isPreferredCustomer() {
      return this.$NsProductUserService.isPreferredCustomer;
    },
    isWholesale() {
      if (this.$NsProductUserService.isLoggedIn) {
        return this.$NsProductUserService.isUserWholesale;
      } else {
        return this.$NsProductUserService.isWholesale;
      }
    },
    loyaltyStatusAndLinks() {
      return this.marketConfig.loyalty || {};
    },
    isLoyaltyEnabled() {
      return !this.hideLoyalty && !!this.loyaltyStatusAndLinks.enabled;
    },
    siteUrl() {
      return this.$NsProductAppService.siteUrl;
    },
    imageFallbackSrc() {
      return this.$NsProductAppService.imageFallbackSrc;
    },
    hasContent() {
      if (
        this.product &&
        this.title &&
        (this.image || (this.imageSet && this.imageSet.length > 0))
      ) {
        return true;
      }
      return false;
    },
    showVariantSelector() {
      if (this.showVariantFromEq) {
        return !this.hideVariantSelector;
      } else {
        return (
          !this.hideVariantSelector && (this.isBaseSku || this.isVariantSku)
        );
      }
    },
    showExclusiveOfferMessage() {
      return !!(
        this.isExclusive &&
        !this.userIsQualified &&
        this.exclusiveOfferMessage
      );
    },
    showOriginalCrossedOut() {
      return this.isSale || !!this.marketConfig.showRetailCrossedOut;
    },
    showPriceLabels() {
      return this.marketConfig.showPriceLabels;
    },
    orderTypes() {
      return (this.product || {}).orderTypes || {};
    },
    hasOrderTypeOrder() {
      // !!this.orderTypes[OrderType.ORDER] doesn't work with change detection
      return !!this.orderTypes.order;
    },
    hasOrderTypeAdr() {
      // !!this.orderTypes[OrderType.ADR] doesn't work with change detection
      return !!this.orderTypes.adr;
    },
    /**
     * Show ADR pricing (if not the same) and add to cart, not to be confused with showAdrPrice (used to force the ADR Price to show even if same)
     */
    showAdr() {
      return !this.hideAdr && this.hasOrderTypeAdr;
    },
    /**
     * Show ADR pricing and add to cart instead of standard pricing
     */
    showAdrOnly() {
      return this.showAdr && (this.adrHasPriority || !this.hasOrderTypeOrder);
    }
  },
  watch: {
    quantity(newVal) {
      if (newVal !== this.selectedQuantity) {
        this.selectedQuantity = newVal;
      }
    },
    sku: {
      immediate: true,
      handler(sku) {
        this.addListeners(); // checks if the listeners are already set up
        this.refreshProductData(sku);
      }
    },
    hideAdrOverride() {
      this.setHideAdr();
    }
  },
  async mounted() {
    this.addListeners(); // checks if the listeners are already set up
    await this.init();
  },
  destroyed() {
    this.removeListeners();
  },
  methods: {
    async init() {
      this.setTranslations();
      await this.$nextTick();

      this.setLoyalty();
      this.setHideAdr();
    },

    /**
     * Initializes product data
     */
    async refreshProductData(sku) {
      await waitForConfig();
      if (
        !sku ||
        this.$NsProductAppService.loading ||
        this.$NsProductUserService.loading ||
        this.$NsProductFeatureFlagsService.loading
      ) {
        return;
      }

      this.resetProductData(sku);

      this.isStorefront =
        ShoppingContext.isContext(ShoppingContext.STOREFRONT) &&
        StoreFrontSponsorStorageService.isStorefront();
      this.isPersonalOffer =
        ShoppingContext.isContext(ShoppingContext.PERSONAL_OFFER) &&
        !!(PersonalOfferStorageService.getPersonalOffer() || {}).active;

      this.activeSku = sku;

      this.setFromProductData(sku);

      this.$emit("active-sku", this.activeSku);
    },

    setFromProductData(sku, product) {
      if (!product) {
        product = this.$NsProductDataService.findProduct(sku);
      }

      if (!product) {
        this.$NsProductDataService.queue(sku);
        this.emitAvailability();
        return;
      }

      if (product.isBase()) {
        this.baseSku = product.sku;
        this.variants = product.getVariantsList();
        this.baseProduct = product;

        if (
          this.activeSku === product.sku &&
          this.loadFirstVariant &&
          this.variants.length > 0
        ) {
          this.activeSku = this.variants[0].sku;
        }

        this.setBaseSkuPriceRange();
      } else {
        this.variants = [];
        this.product = product;
      }

      if (
        product.equinoxProductId != undefined &&
        product.equinoxProductId.length > 0
      ) {
        let variantsArr = Object.values(product.variants);
        if (product.variants && variantsArr.length) {
          this.variants = product.getVariantsList();
          this.baseSku = product.sku;
          this.baseProduct = product;
          if (
            this.activeSku === product.sku &&
            this.loadFirstVariant &&
            this.variants.length > 0
          ) {
            this.activeSku = this.variants[0].sku;
          }
          this.showVariantFromEq = true;
        }
      }

      this.appendTranslations({
        variantType: product.variantType,
        variantDropdownLabel: product.variantDropdownLabel,
        variantDropdownPlaceholder: product.variantDropdownPlaceholder
      });

      this.setActiveProduct();
    },

    async setLoyalty() {
      this.hideLoyalty = this.isStorefront || this.isPersonalOffer;
      let localLoyaltyData =
        JSON.parse(localStorage.getItem("loyaltyData")) || null;

      // Making sure they are not already opted in and we have already set hide loyalty
      if (
        localLoyaltyData &&
        !localLoyaltyData.optInStatus &&
        !this.hideLoyalty
      ) {
        this.hideLoyalty = !(await webLoyalty.loyaltyCanOptIn());
      }

      // Check if loyalty is on for the market and not personal offer or mysite, coming from computed value, also checks hideLoyalty
      if (this.isLoyaltyEnabled) {
        // String Defaults
        this.loyaltyStatusAndLinks.landingPageLinkMessage =
          "Your enJoy Rewards are waiting!";
        this.loyaltyStatusAndLinks.dashboardLinkMessage =
          "enJoy Rewards available!";
        this.loyaltyStatusAndLinks.notOptedIn =
          "Your enJoy Rewards are waiting";
        this.loyaltyStatusAndLinks.productOptedInEarnAndRedeem =
          "{earnPercentage} enJoy Rewards available!";
        this.loyaltyStatusAndLinks.productOptedInEarnNotRedeem =
          "Earn with Nu Skin Rewards";
        this.loyaltyStatusAndLinks.productOptedInRedeemNotEarn =
          "Save with Nu Skin Rewards";

        StringService.getApplicationStrings("loyalty").then(content => {
          this.loyaltyStrings = content;
          // Link message to show to non-opted in users
          if (this.loyaltyStrings.productDisplayNotOptedinMessage) {
            this.loyaltyStatusAndLinks.landingPageLinkMessage = this.loyaltyStrings.productDisplayNotOptedinMessage;
            this.loyaltyStatusAndLinks.notOptedIn = this.loyaltyStrings.productDisplayNotOptedinMessage;
          }
          // Get loyalty information for user
          const loyaltyData = webLoyalty.getLoyaltyRewardsPoints();

          if (loyaltyData && loyaltyData.optInStatus) {
            if (this.loyaltyStrings.productDisplayOptedinMessage) {
              let earnPercentage = "";
              if (
                loyaltyData.userTier &&
                loyaltyData.userTier.purchaseRatio &&
                !isNaN(loyaltyData.userTier.purchaseRatio)
              ) {
                earnPercentage =
                  Number(
                    Number(loyaltyData.userTier.purchaseRatio * 100).toFixed(2)
                  ) + "%";
              }
              this.loyaltyStatusAndLinks.dashboardLinkMessage = this.loyaltyStrings.productDisplayOptedinMessage.replace(
                /\{earnPercentage\}/g,
                earnPercentage
              );
              this.loyaltyStatusAndLinks.productOptedInEarnAndRedeem = this.loyaltyStrings.productDisplayOptedinMessage.replace(
                /\{earnPercentage\}/g,
                earnPercentage
              );
            }
            if (this.loyaltyStrings.productOptedInEarnNotRedeem) {
              this.loyaltyStatusAndLinks.productOptedInEarnNotRedeem = this.loyaltyStrings.productOptedInEarnNotRedeem;
            }
            if (this.loyaltyStrings.productOptedInRedeemNotEarn) {
              this.loyaltyStatusAndLinks.productOptedInRedeemNotEarn = this.loyaltyStrings.productOptedInRedeemNotEarn;
            }
            this.loyaltyStatusAndLinks.dashboardPage = this.localeReplace(
              this.loyaltyStatusAndLinks.dashboardPage
            );
            this.userLoyalty = loyaltyData;
          }
        });

        this.loyaltyStatusAndLinks.landingPage = this.localeReplace(
          this.loyaltyStatusAndLinks.dashboardPage
        );
        this.loyaltyStatusAndLinks.siteUrl = this.siteUrl;
      }
    },

    localeReplace(url) {
      return url.replace(/\{locale\}/g, this.locale).replace(" ", "");
    },

    /**
     * Sets product to new selection
     */
    async setActiveProduct() {
      if (this.baseProduct && this.baseProduct.isBase()) {
        let activeProduct = this.variants.find(
          variantProduct => variantProduct.sku === this.activeSku
        );
        if (!activeProduct) {
          activeProduct = this.baseProduct;
          this.activeSku = this.baseProduct.sku;
        }
        this.product = activeProduct;
      }

      //Put back this after hot-fix/INC0125636
      if (this.product.sku !== this.activeSku) {
        let activeProduct = this.variants.find(
          variantProduct => variantProduct.sku === this.activeSku
        );
        this.product = activeProduct;
      }

      try {
        if (
          this.product &&
          this.product.variants &&
          Object.keys(this.product.variants).length > 1 &&
          this.product.variants[this.activeSku]
        ) {
          this.title = this.stripNonWhiteListHTML(
            this.product.variants[this.activeSku].title
          );
        } else {
          this.title = this.stripNonWhiteListHTML(
            this.product.getProductSearchTitle()
          );
        }
        this.description = this.product.longDescr;

        this.isBaseSku = this.product.isBase();
        this.isVariantSku = this.product.isVariant();

        this.isExclusive = this.product.isExclusive;
        this.nettoWeight = this.stripNonWhiteListHTML(this.product.nettoWeight);
        this.sizeWeight = this.stripNonWhiteListHTML(this.product.sizeWeight);
        this.eventName = this.product.eventName;
        this.salesEventText = this.product.salesEventText;

        this.marketAttributes = this.product.marketAttributes || {};

        this.imageSet = (this.product.getProductCarouselImages() || []).map(
          img => ({
            ...img,
            src: this.$NsProductAppService.getContentPath(img.src)
          })
        );
        const src = (this.product.fullImage
          ? this.product.fullImage
          : this.imageSet.length > 0
          ? this.imageSet[0].src
          : ""
        ).replace(/240.240./g, "");
        this.image = {
          src: this.$NsProductAppService.getContentPath(src),
          alt: this.product.imageAltText || "",
          contentType: "img"
        };
      } catch (err) {
        //console.error(err);
      }

      let isBundle = false;
      if (this.product.childSkus && this.product.childSkus.length) {
        this.product.childSkus.forEach(product => {
          if (product.type === BUNDLE) {
            isBundle = true;
          }
        });
      }
      if (isBundle) {
        const promotion = await fetchPromotionalPrices(this.product);
        if (promotion) {
          if (promotion.price) {
            this.promotionPrice = promotion.price;
            this.price = promotion.price;
            this.product.setPrice(promotion.price);
          }
          if (promotion.originalPrice) {
            this.promotionOriginalPrice = promotion.originalPrice;
            this.originalPrice = promotion.originalPrice;
          }
        }
        //this.$emit("quantity-select", this.selectedQuantity);
      }

      this.setProductPricingAndAvailability();
      this.$emit("product-update", this.product);
    },

    handleProductChanged(product) {
      const baseProductChanged =
        product.isBase() &&
        (this.baseSku === product.sku ||
          product.variantSkus().includes(this.activeSku));
      const productChanged =
        !baseProductChanged &&
        (this.activeSku === product.sku ||
          this.activeSku === product.equinoxProductId);
      if (baseProductChanged || productChanged) {
        this.setFromProductData(this.activeSku, product);
      }
    },

    /**
     * Sets product availability/qualifications (ability to add product to cart) by controlling the maxQuantity
     */
    setProductAvailability() {
      if (this.product) {
        // WARNING: There's a instanceof check on CartService.getAddToCartQty for the ns-shop Product, previously different
        const shopProduct = new ShopProduct(this.product);

        getConfiguration(["Equinox_Markets"]).then(({ Equinox_Markets }) => {
          const { active } = Equinox_Markets;

          const cartService = active ? EquinoxCartService : CartService;
          // check if a user is qualified to purchase the product
          cartService
            .getAddToCartQty(shopProduct)
            .then(productQuantity => {
              // WARNING: this.product is volatile here. re-check is using this.product.
              this.maxQuantity = Math.min(
                shopProduct.maxQuantity,
                productQuantity
              );
            })
            .catch(error => {
              this.maxQuantity = shopProduct.maxQuantity;
              console.error("Failed to retrieve add to cart quantity.", error);
            })
            .finally(() => {
              this.setStatus();
              this.checkedQualifications = true;
              this.emitAvailability();
            });
        });
      }
    },

    /**
     * Sets add to cart/adr and message based on various criteria
     */
    async setStatus() {
      this.statusMessage = "";
      this.statusMessageKey = "";

      if (!this.product || this.isBaseSku) {
        this.disable();
        return;
      }

      if (this.product.status === ProductStatus.ReleasedForSale) {
        if (this.maxQuantity > 0) {
          this.enable();
          this.statusMessageKey = "releasedForSale";

          if (
            this.product.backOrderDate &&
            this.localTranslations.backOrderedWithDate
          ) {
            this.statusMessage = this.localTranslations.backOrderedWithDate.replace(
              "%date%",
              dateFormatter.getCountryFormattedDate(
                this.product.backOrderDate,
                this.locale
              )
            );
            this.statusMessageKey = "backOrderedWithDate";

            let backOrderDate = new Date(this.product.backOrderDate);
            let today = new Date();
            if (backOrderDate < today) {
              if (this.product.availableQuantity > 0) {
                this.statusMessage = "";
                this.statusMessageKey = "releasedForSale";
                //BRW-4541 -- Only allow to purchase the atpQuantity if backorder date is in the past
                this.maxQuantity = Math.min(
                  this.product.maxQuantity,
                  this.product.availableQuantity
                );
              } else {
                this.statusMessage = this.localTranslations.outOfStock;
                this.statusMessageKey = "outOfStock";
                this.disable();
              }
            }
          }
        } else {
          this.disable();

          if (!this.isExclusive) {
            if (this.product.availableQuantity > 0) {
              this.statusMessage = this.localTranslations.reachedPurchaseLimit;
              this.statusMessageKey = "reachedPurchaseLimit";
            } else {
              this.statusMessage = this.localTranslations.outOfStock;
              this.statusMessageKey = "outOfStock";
            }
          }
        }
      } else {
        this.disable();

        if (this.product.status === ProductStatus.Discontinued) {
          this.statusMessage = this.localTranslations.discontinued;
          this.statusMessageKey = "discontinued";
        } else if (this.product.status === ProductStatus.NotReleasedForSale) {
          this.statusMessage = this.localTranslations.notReleasedForSale;
          this.statusMessageKey = "notReleasedForSale";
        } else if (this.product.status === ProductStatus.StopStatus) {
          this.statusMessage = this.localTranslations.stopStatus;
          this.statusMessageKey = "stopStatus";
        }
      }

      this.exclusiveOfferMessage = "";
      if (this.isExclusive) {
        if (this.isLoggedIn) {
          const productQualification = await QualificationService.getQualification(
            this.activeSku
          );

          let cartQuantity = 0;

          if (
            (await getConfiguration(["Equinox_Markets"])).Equinox_Markets.active
          ) {
            let cartItems = await EquinoxCartService.getEquinoxCart();
            //Check for count cart items or set value to zero
            if (cartItems.data && cartItems.data.items) {
              cartQuantity = cartItems.data.items.reduce((cq, item) => {
                const sku = item.skus.find(s => s.productId === this.activeSku);
                if (sku) {
                  return cq + item.quantity;
                } else {
                  return cq;
                }
              }, 0);
            }
          } else {
            if (!this.baseSku) {
              const cartItem = await CartService.getFirstItemBySku(
                this.activeSku,
                {
                  cartOrderItems: true
                }
              );
              cartQuantity = (cartItem || {}).qty || 0;
            } else {
              const cartData =
                (await QualificationService.convertCartDataToBaseSkus(
                  CartService.getItemData(),
                  true
                )) || {};
              const cartItem = cartData[this.baseSku] || {};
              cartQuantity = cartItem.qty || 0;
            }
          }

          if (cartQuantity > 0) {
            this.maxQuantity =
              productQualification.quantity - cartQuantity > 0
                ? productQualification.quantity - cartQuantity
                : 0;
            this.handleQuantitySelect(1);
          }
          if (this.maxQuantity == 0) {
            this.disable();
          }

          if (!productQualification) {
            // the event has never been set up or is in the past (post-event/qualification not configured)
            this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferNoQualification;
          } else if (
            (this.userIsQualified && !this.maxQuantity) ||
            (!this.userIsQualified && !productQualification.quantity) ||
            productQualification.quantity === cartQuantity
          ) {
            // qualified to purchase but no quantity available anymore OR not qualified and no qualified quantity left (used qualified quantity)
            this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferUsedUpQty;
          } else if (
            !this.userIsQualified &&
            productQualification.quantity > 0
          ) {
            // not qualified to purchase and quantity is available from qualification (pre-event)
            this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferNotAvailable;
          }
        } else {
          // not logged in to receive exclusive offer
          this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferMessage;
        }
      }
    },

    /**
     * Set product pricing
     */
    setStandardPricing() {
      // set price and points
      this.originalPrice = this.product.getPricing(PriceType.WRTL);
      this.priceType = this.getPriceType();
      this.price = this.product.getPricing(this.priceType);
      this.points = this.product.getPvFixed();

      // set labels
      // wholeSalePriceLabel comes from custom productDetail translation and wholesalePriceLabel comes from common strings
      this.priceLabel = this.isWholesale
        ? this.localTranslations.wholeSalePriceLabel ||
          this.localTranslations.wholesalePriceLabel
        : this.localTranslations.retailPriceLabel;

      this.originalPriceLabel = this.localTranslations.retailPriceLabel;
    },

    /**
     * Sets sales event pricing if there is an active event for this sku, or normal pricing if not
     */
    async setProductPricing() {
      const equinoxMarketsConfig = getCachedConfiguration("Equinox_Markets");
      const isEquinoxEnabled =
        equinoxLocalStorage.isEquinoxEnabled() && equinoxMarketsConfig.active;
      const isGraphQLActive = equinoxMarketsConfig.MySite_graphql_active;

      const options = {
        isEqPromotion: isEquinoxEnabled
      };

      this.product.setPriceAndPvFromType(
        this.getPriceType(),
        this.activeSalesEvents,
        options
      );

      // set adr price
      this.adrPriceType = this.isWholesale
        ? PriceType.WADW
        : this.getAdrPriceType();
      this.adrPrice = this.showAdr
        ? this.product.getPricing(this.adrPriceType)
        : "";

      // check if sales event
      this.eventName = this.product.eventName;
      this.salesEventText = this.product.salesEventText;

      if (this.eventName) {
        this.isSale = true;

        // set sales event pricing and points
        this.originalPriceType = this.product.priceType;

        if (isGraphQLActive) {
          this.originalPrice = this.product.getPricing(this.originalPriceType);
          this.product.priceType = `${this.eventName}-${this.originalPriceType}`;
        } else {
          this.product.getOriginalPrice();
        }

        this.priceType = this.product.priceType;
        this.price = this.product.getPricing(this.priceType);

        //If EQ market, always show the discounted price if product has promotion
        //Hence, we set the price to member(wholesale / PriceType.WADW) if the price type PriceType.WADR (retail)
        //this is inline with the change in business rules to always show the discounted prices
        if (isGraphQLActive && this.getAdrPriceType() == PriceType.WADR) {
          this.adrPrice = this.product.getPricing(PriceType.WADW);
        }

        //workaround to hardcoded priceType === PriceType.WRTL in ns-product-lib mapping
        //which makes the PriceType === PriceType.WRTL to render WWHL - WWHL price value
        //for product that has promotion
        //set the price to  WWHL if this.getPriceType() === WRTL
        //promotion price is set in product.js   setPriceAndPvFromType function
        // if (isGraphQLActive && this.getPriceType() == PriceType.WRTL) {
        //   this.price = this.product.getPricing(PriceType.WWHL);
        // }

        if (this.user && this.isDistributor) {
          this.points = this.product.getPvFixed();
        }

        // set labels
        this.salesLabel = this.product.salesLabel || this.salesLabel;
        this.priceLabel = this.localTranslations.salesEventPriceLabel;
        this.originalPriceLabel = this.isWholesale
          ? this.localTranslations.wholeSalePriceLabel ||
            this.localTranslations.wholesalePriceLabel
          : this.localTranslations.retailPriceLabel;
      } else {
        // set standard pricing
        this.salesLabel = "";
        this.isSale = false;
        this.setStandardPricing();
      }

      let isBundle = false;
      if (this.product.childSkus && this.product.childSkus.length) {
        this.product.childSkus.forEach(product => {
          if (product.type === BUNDLE) {
            isBundle = true;
          }
        });
      }
      if (isBundle && isEquinoxEnabled) {
        if (this.promotionPrice) {
          this.price = this.promotionPrice;
          this.product.setPrice(this.promotionPrice);
        }
        if (this.promotionOriginalPrice) {
          this.originalPrice = this.promotionOriginalPrice;
        }
      }
    },

    /**
     * Used to get the price type for the current user
     */
    getPriceType() {
      return this.$NsProductDataService.getPriceType();
    },

    /**
     * Used to get the price type when ADR for the current user
     */
    getAdrPriceType() {
      return this.$NsProductDataService.getAdrPriceType();
    },

    setHideAdr() {
      const isSkinConsult = util.isSkinConsult();
      const mySiteHideAdr = !!(
        this.marketConfig &&
        this.marketConfig.storefront &&
        this.marketConfig.storefront.mySiteHideAdr
      );
      this.hideAdr =
        !this.adrHasPriority &&
        (this.hideAdrOverride ||
          ((this.isStorefront || isSkinConsult) && mySiteHideAdr));
    },

    goToDetails() {
      SitesSessionStorageService.writeItem(KEY_BREADCRUMB_BACK, true);

      if (window.dataLayer && window.ga && window.ga.loaded) {
        let impression = {
          event: events.shop.PRODUCT_IMPRESSION_CLICK,
          ecommerce: {
            click: {
              actionField: {},
              products: [
                {
                  name: this.product.title,
                  id: this.product.sku,
                  price: this.product.price,
                  catalog: window.location.pathname
                    .split("/")
                    .pop()
                    .split(".")[0],
                  position: 1,
                  domain: window.location.hostname
                }
              ]
            }
          },
          eventCallback: () => {
            this.redirectToDetailsPage();
          }
        };
        window.dataLayer.push(impression);
      } else {
        this.redirectToDetailsPage();
      }
    },

    redirectToDetailsPage() {
      const equinoxConfiguration = getCachedConfigurations(["Equinox_Markets"])
        .Equinox_Markets;
      const sponsor = StoreFrontSponsorStorageService.getStoreFrontSponsor();
      const sponsorSubdomain = this.isStorefront
        ? Object.keys(sponsor.storefront || {})[0] || ""
        : "";
      const baseUrl =
        window.location.hostname === "localhost"
          ? this.isStorefront
            ? `https://mysite${
                this.runConfig.envCode === "test" ? ".test" : ""
              }.mynuskin.com`
            : this.marketConfig.siteUrl
          : window.location.origin;
      let pdpPath = `products/product.${this.product.sku}`;
      if (this.isStorefront || this.isPersonalOffer) {
        pdpPath += ".mysite";
      }
      if (this.isStorefront) {
        pdpPath += `.${sponsorSubdomain}`;
      }
      pdpPath += ".html";
      const detailsPageUrl = `${getFullUrl(
        baseUrl,
        UrlService.getAemLocaleUrl()
      )}${pdpPath}`;

      // Disable redirect for Equinox Enabled Personal Offer Page
      const disableRedirect =
        equinoxConfiguration.active && this.isPersonalOffer;

      if (!disableRedirect) {
        window.location = detailsPageUrl;
      }
    },

    handleVariantSelect(variantSku) {
      this.activeSku = variantSku;
      this.setActiveProduct();

      this.$emit("variant-select", variantSku);
    },

    handleQuantitySelect(quantity) {
      this.selectedQuantity = quantity;

      if (
        +this.selectedQuantity > this.maxQuantity ||
        +this.selectedQuantity < 1 ||
        +this.selectedQuantity % 1 !== 0
      ) {
        this.disableAddToCart = true;
        this.disableAddToAdr = true;

        this.$emit("disable-add-to-cart", quantity);
      } else if (this.userIsQualified) {
        this.checkAddToCartAndAdr();
      }

      this.$emit("quantity-select", quantity);
      this.emitAvailability();
    },

    emitAvailability() {
      this.$emit("availability", {
        isBaseSku: this.isBaseSku,
        isVariantSku: this.isVariantSku,
        addToCart: !this.disableAddToCart,
        addToAdr: !this.disableAddToCart,
        selectedQuantity: Number(this.selectedQuantity),
        maxQuantity: this.maxQuantity,
        originalPrice: this.originalPrice,
        price: this.price,
        adrPrice: this.adrPrice
      });
    },

    /**
     * Adds product to cart
     */
    handleAddToCart() {
      if (!this.disableAddToCart) {
        // Disable the add to cart button on click
        this.disableAddToCart = true;
        this.disableAddToAdr = true;
        window.sessionStorage.setItem("nstoast-allowed", "true");

        let countryCode = CartService.getCartProperty("cntryCd") || "";

        let options = {
          sku: this.activeSku,
          product: this.product,
          qty: this.selectedQuantity,
          userId: this.userId,
          cntryCd: countryCode,
          adr: this.showAdrOnly,
          referrer: this.referrer,
          domain: window.location.hostname
        };

        const country = RunConfigService.getRunConfig().country;
        getConfiguration(["Equinox_Markets"]).then(config => {
          const countryCode = config.Equinox_Markets.country_code;
          if (
            countryCode === country &&
            equinoxLocalStorage.isEquinoxEnabled() &&
            config.Equinox_Markets.active
          ) {
            ProductDataService.getProductData(options.sku)
              .then(productData => {
                let productToAdd = productData.products[0];
                const productIdentifier =
                  productData.products[0].equinoxProductId;
                if (Object.keys(productToAdd.variants).length > 1) {
                  productToAdd = productData.products[0].variants[options.sku];
                  productToAdd.equinoxProductId = productIdentifier;
                  //productToAdd.properties = productData.products[0].properties
                }
                if (
                  productToAdd.backOrderDate &&
                  new Date(productToAdd.backOrderDate) > new Date()
                ) {
                  productToAdd.inventory = "BACKORDER";
                  productToAdd.properties.isBackOrdered = true;
                  productToAdd.properties.inventoryStatus = "BACKORDER";
                }
                options.product = new ShopProduct(productToAdd);
                options.add = {
                  item: {
                    qty: options.qty
                  }
                };
                EquinoxCartService.addProductToEquinoxCart(options)
                  .then(() => {
                    events.publish(events.shop.ADD_TO_CART, options);
                    this.setStatus();
                  })
                  .catch(err => {
                    // TODO
                    console.error(err);
                    this.setStatus();
                  });
              })
              .catch(error => {
                console.log(`ProductDataService.getProductData: `, error);
              });
          } else {
            options.product = this.product;
            CartService.addProductToCart(options)
              .then(() => {
                this.maxQuantity -= options.qty;
                this.setStatus();
                events.publish(events.shop.ADD_TO_CART, options);
                // don't show the Polymer add to bag modal unless required
                // events.publish(events.shop.SHOW_ADD_TO_BAG, options);
                this.$emit("added-to-cart", options);
              })
              .catch(error => {
                console.error("Failed to add to cart.", error);
                this.setStatus();
              });
          }
        });
      }
    },

    /**
     * Adds product to subscription order
     */
    handleAddToAdr() {
      var self = this;
      if (!this.disableAddToAdr) {
        this.disableAddToAdr = true;
        getConfiguration(["Equinox_Markets"]).then(config => {
          let options = {
            sku: this.activeSku,
            qty: this.selectedQuantity,
            adr: !AdrService.isAdrOverride(),
            referrer: this.referrer
          };
          if (
            config.Equinox_Markets.active &&
            equinoxLocalStorage.isEquinoxEnabled()
          ) {
            ProductDataService.getProductData(this.activeSku).then(
              productData => {
                options.product = new ShopProduct(productData.products[0]);

                EquinoxCartService.addProductToEquinoxCart(options)
                  .then(() => {
                    self.maxQuantity -= options.qty;
                    self.setStatus();
                    events.publish(events.shop.ADD_TO_CART, options);
                    events.publish(events.shop.SHOW_ADD_TO_BAG, options);
                    self.$emit("added-to-adr", options);
                  })
                  .catch(error => {
                    console.error("Failed to add to ADR.", error);
                    this.setStatus();
                  });
              }
            );
          } else {
            options.product = this.product;
            CartService.addProductToCart(options)
              .then(() => {
                this.maxQuantity -= options.qty;
                this.setStatus();
                events.publish(events.shop.ADD_TO_CART, options);
                events.publish(events.shop.SHOW_ADD_TO_BAG, options);
                this.$emit("added-to-adr", options);
              })
              .catch(error => {
                console.error("Failed to add to ADR.", error);
                this.setStatus();
              });
          }
        });
      }
    },

    /**
     * If listener exists, emit it, else go to the product details page
     */
    handleCardClick() {
      if (this.$listeners["card-click"]) {
        this.$emit("card-click", this.product);
        this.goToDetails();
      } else {
        this.goToDetails();
      }
    },

    /**
     * Sets the main image from an index in imageSet
     */
    handleImageSelect(slideIndex) {
      this.image = {
        src: this.imageSet[slideIndex].src,
        alt: this.imageSet[slideIndex].alt,
        contentType: this.imageSet[slideIndex].contentType
      };
    },

    /**
     * Set product is a pending or current favorite
     */
    setIsFavorite() {
      let pending = JSON.parse(localStorage.getItem(PENDING_FAVORITE)) || null;
      if (this.isLoggedIn && pending && pending.sku === this.activeSku) {
        FavoritesService.addToFavorites(this.activeSku).then(() => {
          this.isFavorite = true;
        });
        localStorage.removeItem(PENDING_FAVORITE);
      } else if (this.isLoggedIn) {
        FavoritesService.isInFavorites(this.activeSku).then(data => {
          this.isFavorite = data;
        });
      }
    },

    /**
     * Adds product to favorites, or requests authentication
     */
    handleFavoriteClick() {
      if (this.isLoggedIn) {
        if (this.isFavorite) {
          FavoritesService.removeFromFavorites(this.activeSku);
        } else {
          FavoritesService.addToFavorites(this.activeSku);
        }
        this.isFavorite = !this.isFavorite;
        this.$emit("favorite-click", {
          sku: this.activeSku,
          isFavorite: this.isFavorite
        });
      } else {
        localStorage.setItem(
          PENDING_FAVORITE,
          JSON.stringify({ sku: this.activeSku })
        );
        events.publish(events.authentication.REQUEST_FOR_LOGIN);
      }
    },

    /**
     * Disables input fields
     */
    disable() {
      this.userIsQualified = false;
      this.disableAddToCart = true;
      this.disableAddToAdr = true;
      this.disableQuantitySelector = true;
    },

    /**
     * Enables add and quantity fields depending if order type allows it
     */
    enable() {
      this.userIsQualified = true;
      this.checkAddToCartAndAdr();
      this.disableQuantitySelector =
        this.disableAddToCart && this.disableAddToAdr;
    },

    checkAddToCartAndAdr() {
      this.disableAddToCart = this.showAdrOnly || !this.hasOrderTypeOrder;
      this.disableAddToAdr = !this.showAdr;
    },

    /**
     * Set price type, pricing, availability, etc.
     */
    setProductPricingAndAvailability() {
      if (this.product) {
        this.setProductPricing();
        this.setProductAvailability();
        this.setIsFavorite();
      }
    },

    /**
     * Handle product status events
     */
    handleProductStatus(results) {
      this.$NsProductDataService.handleProductStatus(results);
    },

    setBaseSkuPriceRange() {
      // We have to use getProp() instead of variantProduct.getPricing
      // because that was returning an array of undefined prices
      const variantPrices = this.variants.map(variantProduct =>
        getProp(variantProduct.priceMap, this.getPriceType(), 0)
      );
      this.minPrice = Math.min(...variantPrices).toString();
      this.maxPrice = Math.max(...variantPrices).toString();
    },

    setUser() {
      this.$NsProductUserService.setUser();
      this.$NsProductFeatureFlagsService.setFeatureFlags();
    },

    /**
     * Set active sales events
     */
    setActiveSalesEvents(activeSalesEvents) {
      if (activeSalesEvents && Array.isArray(activeSalesEvents)) {
        this.activeSalesEvents = activeSalesEvents;
      } else {
        this.activeSalesEvents = [];
      }
      this.setProductPricingAndAvailability();
    },

    handleFeatureFlagsChanged() {
      this.featureFlags = this.$NsProductFeatureFlagsService.featureFlags;
      this.setProductPricingAndAvailability();
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      if (!this.listenersAdded) {
        this.listenersAdded = true;
        // watch app service, user service, and feature flag service finished loading
        this.$watch(
          function() {
            return (
              this.$NsProductAppService.loading ||
              this.$NsProductUserService.loading ||
              this.$NsProductFeatureFlagsService.loading
            );
          },
          async () => {
            await this.init();
            this.refreshProductData(this.activeSku);
          }
        );

        // listen to config changes
        this.$NsProductAppService.$on("config-changed", async () => {
          await this.init();
          this.refreshProductData(this.activeSku);
        });

        // listen to translation changes
        this.$NsProductAppService.$on("translations-changed", translations => {
          this.prependTranslations(translations);
        });

        // listen for run config changes
        events.subscribe(
          events.global.RUN_CONFIG_CHANGED,
          this.$NsProductAppService.checkConfig
        );

        // listen to user changes
        events.subscribe(events.authentication.LOGIN, this.setUser);
        events.subscribe(events.authentication.LOGOUT, this.setUser);

        // get latest active sales events, listen to changes
        events.getValue(
          events.salesevent.ACTIVE_EVENTS,
          this.setActiveSalesEvents,
          true // subscribe
        );

        // listen to product user changes
        this.$NsProductUserService.$on(
          "user-changed",
          this.setProductPricingAndAvailability
        );

        // listen to feature flag changes
        this.$NsProductFeatureFlagsService.$on(
          "feature-flags-changed",
          this.handleFeatureFlagsChanged
        );

        this.$NsProductDataService.$on(
          "product-changed",
          this.handleProductChanged
        );

        // listen to product status changes
        events.subscribe(
          events.shop.PRODUCT_STATUS_RESULT,
          this.handleProductStatus
        );

        // get latest qualification status, listen to changes
        events.getValue(
          events.shop.QUALIFICATION_STATUS_CHANGED,
          this.setProductAvailability,
          true // subscribe
        );

        events.publish(events.salesevent.POLL_MARKET_CONFIG);
      }
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to product app service events
      this.$NsProductAppService.$off();

      // stop listening to user changes
      events.unsubscribe(events.authentication.LOGIN, this.setUser);
      events.unsubscribe(events.authentication.LOGOUT, this.setUser);

      // stop listening to active sales events changes
      events.unsubscribe(
        events.salesevent.ACTIVE_EVENTS,
        this.setActiveSalesEvents
      );

      // stop listening to product user service events
      this.$NsProductUserService.$off();

      // stop listening to product feature flag service events
      this.$NsProductFeatureFlagsService.$off();

      // stop listening to product data service events
      this.$NsProductDataService.$off();

      // stop listening to product status changes
      events.unsubscribe(
        events.shop.PRODUCT_STATUS_RESULT,
        this.handleProductStatus
      );

      // stop listening to qualification status changes
      events.unsubscribe(
        events.shop.QUALIFICATION_STATUS_CHANGED,
        this.setProductAvailability
      );
    },

    // set local translations by merging passed in translations, common translations, and currently-set translations
    setTranslations() {
      this.prependTranslations(this.$NsProductAppService.translations);
    },

    // override local translations regardless of source with updated translations
    appendTranslations(translations) {
      this.localTranslations = {
        ...this.localTranslations,
        ...translations
      };
    },
    prependTranslations(serviceTranslations) {
      let translations = {};
      try {
        if (this.translations) {
          translations =
            this.translations instanceof Object &&
            this.translations.constructor === Object
              ? this.translations
              : JSON.parse(this.translations);
        }
      } catch (err) {
        console.warn("Unable to parse translation object.", err);
      }

      this.localTranslations = {
        ...this.localTranslations,
        ...serviceTranslations,
        ...translations
      };
    },

    resetProductData(sku) {
      this.baseProduct = undefined;
      this.baseSku = "";
      this.activeSku = sku;
      this.product = undefined;

      this.adrPrice = "";
      this.adrPriceType = PriceType.WADR;
      this.description = "";
      this.disableAddToCart = true;
      this.disableAddToAdr = true;
      this.disableQuantitySelector = true;
      this.eventName = "";
      this.exclusiveOfferMessage = "";
      this.hideAdr = false;
      this.image = {};
      this.imageSet = [];
      this.isBaseSku = false;
      this.isExclusive = false;
      this.isStorefront = false;
      this.isPersonalOffer = false;
      this.isFavorite = false;
      this.isSale = false;
      this.isVariantSku = false;
      this.maxQuantity = 999;
      this.nettoWeight = "";
      this.originalPrice = "";
      this.originalPriceLabel = "";
      this.originalPriceType = PriceType.WRTL;
      this.points = "";
      this.price = "";
      this.priceLabel = "";
      this.priceType = PriceType.WRTL;
      this.selectedQuantity = this.quantity;
      this.salesEventText = "";
      this.salesLabel = "";
      this.sizeWeight = "";
      this.statusMessage = "";
      this.statusMessageKey = "";
      this.title = "";
      this.userIsQualified = false;
      this.variants = [];

      this.checkedQualifications = false;

      this.marketAttributes = {};
    },

    /**
     * Removes all html not in whitelist
     */
    stripNonWhiteListHTML(text) {
      if (text) {
        let htmlWhiteList = ["sup", "br"];
        const RE = RegExp;
        htmlWhiteList.forEach(html => {
          text = text.replace(new RE("<" + html + ">", "g"), "%" + html + "%");
          text = text.replace(
            new RE("</" + html + ">", "g"),
            "%/" + html + "%"
          );
        });
        text = util.stripHTML(text);
        htmlWhiteList.forEach(html => {
          text = text.replace(new RE("%" + html + "%", "g"), "<" + html + ">");
          text = text.replace(
            new RE("%/" + html + "%", "g"),
            "</" + html + ">"
          );
        });
      }
      return text || "";
    }
  }
};

export default NsProductMixin;
